import { useCallback, useEffect, useRef, useState } from 'react'

import { IncomingMessageNotification } from '@store/Chat/types'

export type ToastPhase = 'entering' | 'visible' | 'exiting'

export type ManagedToast = {
  notification: IncomingMessageNotification
  phase: ToastPhase
}

const MAX_VISIBLE = 3
export const ENTER_MS = 500
export const STACK_SHIFT_MS = 350
const EXIT_MS = STACK_SHIFT_MS
export const AUTO_DISMISS_MS = 5000

export function useManagedToasts(
  storeNotifications: IncomingMessageNotification[],
  onRemove: (id: string) => void
): { toasts: ManagedToast[]; dismissToast: (id: string) => void } {
  const [managed, setManaged] = useState<ManagedToast[]>([])
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  )
  const onRemoveRef = useRef(onRemove)
  onRemoveRef.current = onRemove

  const clearTimer = useCallback((key: string) => {
    const timer = timersRef.current.get(key)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(key)
    }
  }, [])

  const clearAllTimersForToast = useCallback(
    (id: string) => {
      clearTimer(`enter-${id}`)
      clearTimer(`exit-${id}`)
      clearTimer(`dismiss-${id}`)
    },
    [clearTimer]
  )

  const dismissToast = useCallback(
    (id: string) => {
      clearAllTimersForToast(id)
      setManaged(prev =>
        prev.map(item =>
          item.notification.id === id
            ? { ...item, phase: 'exiting' as const }
            : item
        )
      )
    },
    [clearAllTimersForToast]
  )

  useEffect(() => {
    if (storeNotifications.length === 0) {
      timersRef.current.forEach(timer => clearTimeout(timer))
      timersRef.current.clear()
      setManaged([])
      return
    }

    const exitIds: string[] = []

    setManaged(prev => {
      const knownIds = new Set(prev.map(item => item.notification.id))
      const newcomers = storeNotifications.filter(
        notification => !knownIds.has(notification.id)
      )

      if (newcomers.length === 0) {
        return prev
      }

      let next = [...prev]

      for (const notification of newcomers) {
        const activeCount = next.filter(
          item => item.phase === 'entering' || item.phase === 'visible'
        ).length

        if (activeCount >= MAX_VISIBLE) {
          const oldestActive = next.find(
            item => item.phase === 'entering' || item.phase === 'visible'
          )

          if (oldestActive) {
            exitIds.push(oldestActive.notification.id)
            next = next.map(item =>
              item.notification.id === oldestActive.notification.id
                ? { ...item, phase: 'exiting' as const }
                : item
            )
          }
        }

        next = [...next, { notification, phase: 'entering' as const }]
      }

      return next
    })

    exitIds.forEach(id => clearAllTimersForToast(id))
  }, [storeNotifications, clearAllTimersForToast])

  useEffect(() => {
    managed.forEach(toast => {
      const id = toast.notification.id

      if (toast.phase === 'entering' && !timersRef.current.has(`enter-${id}`)) {
        const timer = setTimeout(() => {
          timersRef.current.delete(`enter-${id}`)
          setManaged(prev =>
            prev.map(item =>
              item.notification.id === id
                ? { ...item, phase: 'visible' as const }
                : item
            )
          )
        }, ENTER_MS)
        timersRef.current.set(`enter-${id}`, timer)
      }

      if (
        toast.phase === 'visible' &&
        !timersRef.current.has(`dismiss-${id}`)
      ) {
        const timer = setTimeout(() => {
          timersRef.current.delete(`dismiss-${id}`)
          setManaged(prev =>
            prev.map(item =>
              item.notification.id === id
                ? { ...item, phase: 'exiting' as const }
                : item
            )
          )
        }, AUTO_DISMISS_MS)
        timersRef.current.set(`dismiss-${id}`, timer)
      }

      if (toast.phase === 'exiting' && !timersRef.current.has(`exit-${id}`)) {
        const timer = setTimeout(() => {
          timersRef.current.delete(`exit-${id}`)
          clearAllTimersForToast(id)
          setManaged(prev => prev.filter(item => item.notification.id !== id))
          onRemoveRef.current(id)
        }, EXIT_MS)
        timersRef.current.set(`exit-${id}`, timer)
      }
    })
  }, [managed, clearAllTimersForToast])

  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer))
      timersRef.current.clear()
    }
  }, [])

  return { toasts: managed, dismissToast }
}
